import { useState, useRef, useEffect } from "react";
import { Button } from "react-bootstrap";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";

export default function ThumbnailSelector({ videoUrl, onSelect }) {
  const [frames, setFrames] = useState([]);
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cropperRef = useRef(null);

  const captureFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    try {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        throw new Error("Failed to get canvas context");
      }
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const frameData = canvas.toDataURL("image/jpeg");
      setFrames([...frames, frameData]);
      setSelectedFrame(frameData); // Automatically select the captured frame
      const { width, height } = video.getBoundingClientRect();
      if (video.videoHeight > video.videoWidth) {
        setImageDimensions({
          width: video?.videoWidth / 5,
          height: video?.videoHeight / 5,
        });
      } else {
        setImageDimensions({ width: width, height: height });
      }
    } catch (err) {
      setError("Error capturing frame: " + err.message);
    }
  };

  const cropImage = () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      const croppedData = cropper.getCroppedCanvas().toDataURL("image/jpeg");
      onSelect(croppedData); // Pass the cropped image to the parent component
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    setError(null);
    setIsLoading(true);

    const handleError = (e) => {
      setError("Error loading video: " + e.message);
      setIsLoading(false);
    };

    const handleLoadedData = () => {
      setIsLoading(false);
    };

    video.onerror = handleError;
    video.onloadeddata = handleLoadedData;
    video.crossOrigin = "anonymous";
    video.src = videoUrl;

    return () => {
      video.onerror = null;
      video.onloadeddata = null;
    };
  }, [videoUrl]);
  return (
    <div className="mt-8">
      <h3 className="text-lg font-medium mb-4">Select Thumbnail</h3>

      {isLoading && <div className="text-gray-500 mb-4">Loading video...</div>}
      {error && <div className="text-red-500 mb-4">{error}</div>}

      <div className="mb-4">
        <video
          ref={videoRef}
          crossOrigin="anonymous"
          controls
          className="w-full aspect-video"
          style={{
            width: "90%",
            height: "auto",
            maxHeight: "300px",
            objectFit: "contain",
          }}
        />
      </div>

      <Button
        variant="secondary"
        className="mb-4 w-full"
        onClick={captureFrame}
        disabled={frames.length >= 1}
      >
        Capture Current Frame
      </Button>

      <canvas
        ref={canvasRef}
        style={{
          display: "none",
          position: "absolute",
          visibility: "hidden",
        }}
      />

      {selectedFrame && (
        <div className="mb-4">
          <Cropper
            src={selectedFrame}
            style={{
              maxWidth: `${imageDimensions.width}px`,
              maxHeight: `${imageDimensions.height}px`,
            }}
            zoomable={false}
            scalable={false}
            initialAspectRatio={1} // Match display aspect ratio
            aspectRatio={1}
            guides={true}
            viewMode={1}
            minCropBoxHeight={10}
            minCropBoxWidth={10}
            background={false}
            responsive={true}
            checkOrientation={false}
            ref={cropperRef}
          />
        </div>
      )}

      {selectedFrame && (
        <Button variant="primary" className="mt-4 w-full" onClick={cropImage}>
          Confirm Thumbnail
        </Button>
      )}
    </div>
  );
}
